import type { MigrationStrategies, RxJsonSchema } from 'rxdb';

export type RxdbMigrationResult<TDoc> = TDoc | Promise<TDoc>;

export interface RxdbMigrationStep<FromDoc, ToDoc, Version extends number> {
  readonly version: Version;
  readonly migrate: (doc: FromDoc) => RxdbMigrationResult<ToDoc>;
}

type AnyRxdbMigrationStep = RxdbMigrationStep<any, any, number>;

type FromDoc<TStep> = TStep extends RxdbMigrationStep<infer From, any, number> ? From : never;
type ToDoc<TStep> = TStep extends RxdbMigrationStep<any, infer To, number> ? To : never;

type IsExactLink<Left, Right> =
  [ToDoc<Left>] extends [FromDoc<Right>]
    ? [FromDoc<Right>] extends [ToDoc<Left>]
      ? true
      : false
    : false;

export type EnsureRxdbMigrationChain<TSteps extends readonly AnyRxdbMigrationStep[]> =
  TSteps extends readonly [
    infer First extends AnyRxdbMigrationStep,
    infer Second extends AnyRxdbMigrationStep,
    ...infer Rest extends AnyRxdbMigrationStep[],
  ]
    ? IsExactLink<First, Second> extends true
      ? readonly [First, ...EnsureRxdbMigrationChain<readonly [Second, ...Rest]>]
      : never
    : TSteps;

export function defineRxdbMigration<FromDoc, ToDoc, const Version extends number>(
  version: Version,
  migrate: (doc: FromDoc) => RxdbMigrationResult<ToDoc>,
): RxdbMigrationStep<FromDoc, ToDoc, Version> {
  return { version, migrate };
}

export function defineRxdbMigrations<const TSteps extends readonly AnyRxdbMigrationStep[]>(
  ...steps: TSteps & EnsureRxdbMigrationChain<TSteps>
): TSteps {
  return steps;
}

export function toRxdbMigrationStrategies(
  steps: readonly AnyRxdbMigrationStep[],
): MigrationStrategies {
  return Object.fromEntries(
    steps.map((step) => [step.version, step.migrate]),
  ) as MigrationStrategies;
}

export interface RxdbCollectionModel<CollectionName extends string, DocType> {
  readonly collection: CollectionName;
  readonly schema: RxJsonSchema<DocType>;
  readonly migrationStrategies?: MigrationStrategies;
}

export function defineRxdbCollectionModel<
  const CollectionName extends string,
  DocType,
>(
  model: RxdbCollectionModel<CollectionName, DocType>,
): RxdbCollectionModel<CollectionName, DocType> {
  return model;
}
